import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { getViewSetting, setViewSetting } from "../../api/page/viewSetting";
import { ActionType, RootState } from "../../reducer";
import { SetViewSettingAction } from "../../reducer/note/viewSetting";
import {
  PageViewSetting,
  ViewFilter,
  ViewSort,
} from "../../types/notebook/viewSetting";
import { useSelectedDatabaseViewId } from "./view";

export const useViewSetting = (viewId: string | undefined): PageViewSetting | undefined => {
  return useSelector((state: RootState) =>
    viewId ? state.ui.pageViewSettings[viewId] : undefined
  );
};

export const useFetchViewSetting = () => {
  const dispatch = useDispatch();
  return async (viewId: string) => {
    const setting = await getViewSetting({ "view-id": viewId });
    if (!setting || !setting["view-id"]) return; // empty {} means no setting stored
    const action: SetViewSettingAction = {
      type: ActionType.SET_VIEW_SETTING,
      viewId,
      setting,
    };
    dispatch(action as any);
  };
};

export const useFetchViewSettingListener = (viewId: string | undefined) => {
  const fetchViewSetting = useFetchViewSetting();
  useEffect(() => {
    if (!viewId) return;
    fetchViewSetting(viewId);
  }, [viewId]);
};

export const useSetViewSort = () => {
  const dispatch = useDispatch();
  const [selectedViewId] = useSelectedDatabaseViewId();
  const currentSetting = useViewSetting(selectedViewId);

  return async (sort: ViewSort | undefined) => {
    if (!selectedViewId) return;
    const base = currentSetting ?? { "view-id": selectedViewId };
    // Mutual exclusion: strip both sort and filter, then apply only sort
    const { filter: _f, sort: _s, ...baseClean } = { ...base, "view-id": selectedViewId };
    const updated: PageViewSetting = sort
      ? { ...baseClean, sort }
      : baseClean;
    await setViewSetting(updated);
    const action: SetViewSettingAction = {
      type: ActionType.SET_VIEW_SETTING,
      viewId: selectedViewId,
      setting: updated,
    };
    dispatch(action as any);
  };
};

export const useSetViewFilter = () => {
  const dispatch = useDispatch();
  const [selectedViewId] = useSelectedDatabaseViewId();
  const currentSetting = useViewSetting(selectedViewId);

  return async (filter: ViewFilter | undefined) => {
    if (!selectedViewId) return;
    const base = currentSetting ?? { "view-id": selectedViewId };
    // Mutual exclusion: strip both sort and filter, then apply only filter
    const { filter: _f, sort: _s, ...baseClean } = { ...base, "view-id": selectedViewId };
    const updated: PageViewSetting = filter
      ? { ...baseClean, filter }
      : baseClean;
    await setViewSetting(updated);
    const action: SetViewSettingAction = {
      type: ActionType.SET_VIEW_SETTING,
      viewId: selectedViewId,
      setting: updated,
    };
    dispatch(action as any);
  };
};

export const useSetViewCardProps = () => {
  const dispatch = useDispatch();
  const [selectedViewId] = useSelectedDatabaseViewId();
  const currentSetting = useViewSetting(selectedViewId);

  return async (cardProps: string[]) => {
    if (!selectedViewId) return;
    const base = currentSetting ?? { "view-id": selectedViewId };
    // Preserve sort/filter; only replace card-props.
    const updated: PageViewSetting = {
      ...base,
      "view-id": selectedViewId,
      "card-props": cardProps,
    };
    await setViewSetting(updated);
    const action: SetViewSettingAction = {
      type: ActionType.SET_VIEW_SETTING,
      viewId: selectedViewId,
      setting: updated,
    };
    dispatch(action as any);
  };
};
